import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Setup } from "../api";

function useTheme(): "dark" | "light" {
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof document === "undefined") return "dark";
    return (document.documentElement.dataset.theme as "dark" | "light") || "dark";
  });
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const t = document.documentElement.dataset.theme;
      setTheme(t === "light" ? "light" : "dark");
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => obs.disconnect();
  }, []);
  return theme;
}

function fmtPct(v: unknown) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(0)}%` : "—";
}

function fmtNum(v: unknown, digits = 2) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : "—";
}

export default function SetupCard({ s }: { s: Setup }) {
  const theme = useTheme();
  const dirColor = s.direction === "bull" ? "var(--good)" : "var(--bad)";
  const breakout = s.breakout_direction as string | null | undefined;
  const params = new URLSearchParams({
    symbol: s.symbol,
    tf: s.timeframe,
    leg_start_date: String(s.leg_start_date ?? ""),
    tr_end_date: String(s.tr_end_date ?? ""),
  });
  if (s.setup_id) params.set("setup_id", String(s.setup_id));
  const sid = s.setup_id ? String(s.setup_id) : "";
  return (
    <Link
      to={`/detail?${params.toString()}`}
      state={{ setup: s }}
      className="card-link"
    >
    <div className="card">
      <div className="card-row">
        <div className="card-title">
          <strong>{s.symbol}</strong>
          <span className="muted"> · {s.timeframe}</span>
          <span className="dir-pill" style={{ color: dirColor, borderColor: dirColor }}>
            {String(s.direction).toUpperCase()}
          </span>
        </div>
        <div className="card-score-stack">
          <div className="card-score">{Number(s.score).toFixed(1)}</div>
          {typeof s.score_ml === "number" && (
            <div className="card-score-ml" title="ML quality score (GBM)">
              ml {(s.score_ml * 100).toFixed(0)}
            </div>
          )}
        </div>
      </div>
      {sid && (
        <img
          src={`/api/setups/${encodeURIComponent(sid)}/snapshot?theme=${theme}`}
          alt=""
          loading="lazy"
          decoding="async"
          className="card-snapshot"
        />
      )}
      <div className="card-row muted small">
        <div>
          {String(s.leg_start_date ?? "")} → {String(s.tr_end_date ?? "")}
        </div>
        <div>{String(s.origin)}</div>
      </div>
      <div className="card-row small">
        <div className="muted">range height</div>
        <div>{fmtPct(s.tr_height_leg_pct)} of leg</div>
      </div>
      <div className="card-row small">
        <div className="muted">target</div>
        <div>{fmtNum(s.measured_move_target)}</div>
      </div>
      {breakout && (
        <div className="card-row small">
          <div className="muted">breakout</div>
          <div>{breakout}</div>
        </div>
      )}
      {typeof s.p_breakout_within_10 === "number" && (
        <div className="card-row small">
          <div className="muted">p(breakout ≤ 10)</div>
          <div>{(s.p_breakout_within_10 * 100).toFixed(0)}%</div>
        </div>
      )}
    </div>
    </Link>
  );
}
